import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, User, CheckCircle, Clock, FileText, ChevronRight, QrCode, Download, Loader2 } from 'lucide-react';
export function MemberDashboard() {
  const [paymentState, setPaymentState] = useState<'idle' | 'processing' | 'success'>('idle');
  const handlePayment = () => {
    setPaymentState('processing');
    setTimeout(() => {
      setPaymentState('success');
    }, 1500);
  };
  const transactions = [{
    id: 1,
    title: 'Tennis Court Booking',
    date: 'Oct 24, 2026',
    amount: '₹500',
    status: 'Paid'
  }, {
    id: 2,
    title: 'Restaurant Tab',
    date: 'Oct 22, 2026',
    amount: '₹1,200',
    status: 'Pending'
  }, {
    id: 3,
    title: 'Monthly Subscription',
    date: 'Oct 01, 2026',
    amount: '₹800',
    status: 'Paid'
  }];
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="min-h-screen bg-club-canvas py-12 px-4 sm:px-6 lg:px-8" data-id="element-1062">
      <div className="max-w-3xl mx-auto space-y-8" data-id="element-1063">
        {/* Header */}
        <motion.div {...fadeIn} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-id="element-1064">
          <div data-id="element-1065">
            <span className="eyebrow block mb-2" data-id="element-1066">Member Portal</span>
            <h1 className="text-4xl font-serif text-club-green" data-id="element-1067">
              Welcome back, Member
            </h1>
          </div>
          <Link to="/members/card" className="inline-flex items-center gap-2 text-sm font-medium text-club-gold hover:text-club-green transition-colors self-start sm:self-auto" data-id="element-1068">
            <QrCode size={16} data-id="element-1069" />
            View Digital Card
          </Link>
        </motion.div>

        {/* Balance Card */}
        <motion.div className="bg-club-green text-white p-8 rounded-sm relative overflow-hidden shadow-sm" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-1070">
          {/* Decorative pattern */}
          <div className="absolute top-0 right-0 opacity-5 pointer-events-none transform translate-x-1/4 -translate-y-1/4" data-id="element-1071">
            <svg width="240" height="240" viewBox="0 0 100 100" data-id="element-1072">
              <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-1073" />
              <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-1074" />
              <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-1075" />
            </svg>
          </div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6" data-id="element-1076">
            <div data-id="element-1077">
              <p className="text-club-canvas/80 text-sm uppercase tracking-wider mb-2 font-medium" data-id="element-1078">
                Outstanding Balance
              </p>
              <p className="text-5xl font-serif text-club-gold" data-id="element-1079">₹2,500</p>
            </div>
            <button onClick={handlePayment} className="btn-gold whitespace-nowrap flex items-center justify-center gap-2 w-full md:w-auto py-3 px-6" data-id="element-1080">
              <CreditCard size={18} data-id="element-1081" />
              Pay Now (UPI / Card)
            </button>
          </div>
        </motion.div>

        {/* Payment Modal */}
        <AnimatePresence data-id="element-1082">
          {paymentState !== 'idle' && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-club-navy/60 backdrop-blur-sm" data-id="element-1083">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-white rounded-sm max-w-md w-full p-8 shadow-xl relative overflow-hidden" data-id="element-1084">
                {paymentState === 'processing' ? <div className="flex flex-col items-center justify-center py-12 space-y-4" data-id="element-1085">
                    <Loader2 className="w-12 h-12 text-club-gold animate-spin" data-id="element-1086" />
                    <p className="text-lg font-serif text-club-green" data-id="element-1087">
                      Processing Payment...
                    </p>
                    <p className="text-sm text-club-ink/60" data-id="element-1088">
                      Please do not close this window.
                    </p>
                  </div> : <div className="flex flex-col items-center text-center" data-id="element-1089">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 20,
                delay: 0.1
              }} className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6" data-id="element-1090">
                      <CheckCircle className="w-10 h-10 text-green-600" data-id="element-1091" />
                    </motion.div>

                    <h2 className="text-3xl font-serif text-club-green mb-2" data-id="element-1092">
                      Payment Successful
                    </h2>
                    <p className="text-club-ink/70 mb-8" data-id="element-1093">
                      Your outstanding balance has been cleared.
                    </p>

                    <div className="w-full bg-club-canvasAlt border border-club-border rounded-sm p-5 space-y-4 mb-8 text-left" data-id="element-1094">
                      <div className="flex justify-between items-center pb-4 border-b border-club-border/60" data-id="element-1095">
                        <span className="text-sm text-club-ink/60" data-id="element-1096">
                          Amount Paid
                        </span>
                        <span className="text-xl font-medium text-club-green" data-id="element-1097">
                          ₹2,500
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1098">
                        <span className="text-sm text-club-ink/60" data-id="element-1099">
                          Reference No.
                        </span>
                        <span className="text-sm font-mono text-club-ink" data-id="element-1100">
                          RZP_TXN_987654321
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1101">
                        <span className="text-sm text-club-ink/60" data-id="element-1102">Date</span>
                        <span className="text-sm text-club-ink" data-id="element-1103">
                          {new Date().toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1104">
                        <span className="text-sm text-club-ink/60" data-id="element-1105">
                          Payment Method
                        </span>
                        <span className="text-sm text-club-ink" data-id="element-1106">UPI</span>
                      </div>
                    </div>

                    <div className="w-full space-y-3" data-id="element-1107">
                      <button className="w-full btn-secondary py-3 flex items-center justify-center gap-2 bg-white" data-id="element-1108">
                        <Download size={18} data-id="element-1109" />
                        Download Invoice
                      </button>
                      <button onClick={() => setPaymentState('idle')} className="w-full btn-primary py-3" data-id="element-1110">
                        Return to Dashboard
                      </button>
                    </div>
                  </div>}
              </motion.div>
            </div>}
        </AnimatePresence>

        {/* Summary Grid */}
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-1111">
          <div className="bg-club-canvasAlt border border-club-border p-6 rounded-sm flex items-start gap-4" data-id="element-1112">
            <div className="p-3 bg-white rounded-full shrink-0 shadow-sm" data-id="element-1113">
              <User size={20} className="text-club-gold" data-id="element-1114" />
            </div>
            <div data-id="element-1115">
              <p className="text-xs text-club-ink/60 uppercase tracking-wider mb-1" data-id="element-1116">
                Member ID
              </p>
              <p className="text-lg font-medium text-club-green" data-id="element-1117">#CH-1420</p>
            </div>
          </div>

          <div className="bg-club-canvasAlt border border-club-border p-6 rounded-sm flex items-start gap-4" data-id="element-1118">
            <div className="p-3 bg-white rounded-full shrink-0 shadow-sm" data-id="element-1119">
              <CheckCircle size={20} className="text-green-600" data-id="element-1120" />
            </div>
            <div data-id="element-1121">
              <p className="text-xs text-club-ink/60 uppercase tracking-wider mb-1" data-id="element-1122">
                Membership Status
              </p>
              <p className="text-lg font-medium text-green-700" data-id="element-1123">Active</p>
            </div>
          </div>
        </motion.div>

        {/* Transactions List */}
        <motion.div className="bg-white border border-club-border rounded-sm overflow-hidden shadow-sm" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} data-id="element-1124">
          <div className="p-6 border-b border-club-border flex justify-between items-center bg-club-canvas/30" data-id="element-1125">
            <h2 className="text-xl font-serif text-club-green" data-id="element-1126">
              Recent Transactions
            </h2>
            <button className="text-sm text-club-gold hover:text-club-green transition-colors flex items-center gap-1 font-medium" data-id="element-1127">
              View All <ChevronRight size={16} data-id="element-1128" />
            </button>
          </div>
          <div className="divide-y divide-club-border" data-id="element-1129">
            {transactions.map(tx => <div key={tx.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-club-canvasAlt/30 transition-colors" data-id="element-1130">
                <div className="flex items-start gap-4" data-id="element-1131">
                  <div className="p-2 bg-club-canvasAlt rounded-full shrink-0 mt-1" data-id="element-1132">
                    <FileText size={16} className="text-club-ink/60" data-id="element-1133" />
                  </div>
                  <div data-id="element-1134">
                    <p className="font-medium text-club-ink mb-1" data-id="element-1135">{tx.title}</p>
                    <p className="text-sm text-club-ink/60" data-id="element-1136">{tx.date}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2" data-id="element-1137">
                  <p className="text-lg font-medium text-club-green" data-id="element-1138">
                    {tx.amount}
                  </p>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium ${tx.status === 'Paid' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-orange-50 text-orange-700 border border-orange-200'}`} data-id="element-1139">
                    {tx.status === 'Paid' ? <CheckCircle size={12} data-id="element-1140" /> : <Clock size={12} data-id="element-1141" />}
                    {tx.status}
                  </span>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}